import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useResizeObserver } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { isSameDay } from 'date-fns';
import { useDateFormatter } from '../utils/date-time-format';
import { ScheduleBlock } from './schedule-block';
import { useScheduleContext } from './schedule-context';
import { ScheduleItem } from './schedule-item';

export function RenderSpecialDays() {
  const formatDate = useDateFormatter();
  const { specialIntervals, inSpecialInterval } = useScheduleContext();
  const { t } = useLingui();

  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const [rect] = useResizeObserver(itemsContainerRef.current);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const scrollWidth = itemsContainerRef.current?.scrollWidth ?? 0;
    const width = itemsContainerRef.current?.offsetWidth ?? 0;
    setIsOverflowing(
      (previousIsOverflowing) => previousIsOverflowing || width < scrollWidth
    );
  }, [rect]);

  useEffect(() => {
    setIsOverflowing(false);
  }, [specialIntervals]);

  const hasDescription = useMemo(
    () => specialIntervals.some(({ description }) => !!description),
    [specialIntervals]
  );

  if (specialIntervals.length === 0) {
    return null;
  }

  return (
    <ScheduleBlock
      title={t`Special Days`}
      largeOffsets={hasDescription}
      itemsContainerRef={itemsContainerRef}
      compactMode={isOverflowing}
    >
      {specialIntervals.map(
        ({
          dates: [start, end],
          hours,
          isClosed,
          isToday,
          description,
          id
        }) => {
          const renderDate = () =>
            isSameDay(start, end)
              ? formatDate(start)
              : `${formatDate(start)} - ${formatDate(end)}`;

          return (
            <ScheduleItem
              largeOffset={hasDescription}
              key={id}
              title={renderDate()}
              caption={description}
              hours={hours}
              highlight={isToday && inSpecialInterval}
              isClosed={isClosed}
              compactMode={isOverflowing}
            />
          );
        }
      )}
    </ScheduleBlock>
  );
}
